/**
 * Inventory No Category Exception Config
 *
 * Covers: ex-inv-ls-no-category
 *
 * Products in Lightspeed without a category assigned.
 * Simple table with product name (LS link), SKU, and price.
 */
import { formatCurrency } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  activePart,
  composeRowSubtitle,
  moneyPart,
  skuPart,
} from '../shared/rowSubtitle'
import {
  productStatusSortValue,
  productTypeSortValue,
  renderProductName,
  renderProductStatus,
  renderProductType,
} from '../shared/productRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const PRODUCT_NAME_FROZEN_WIDTH = 350

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Product Name',
    align: 'left',
    width: PRODUCT_NAME_FROZEN_WIDTH,
    minWidth: PRODUCT_NAME_FROZEN_WIDTH,
    freeze: 'left',
    render: renderProductName,
    link: { field: 'lightspeed_product_link', system: 'lightspeed' },
  },
  {
    key: 'active',
    label: 'Product Status',
    align: 'center',
    render: renderProductStatus,
    getSortValue: productStatusSortValue,
  },
  {
    key: 'product_type',
    label: 'Type',
    align: 'center',
    render: renderProductType,
    getSortValue: productTypeSortValue,
  },
  {
    key: 'sku',
    label: 'SKU',
    render: (value) => value || 'N/A',
  },
  {
    key: 'price_including_tax',
    label: 'Price',
    type: 'currency',
    align: 'right',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const noCategoryConfig: ExceptionConfig = {
  codes: ['ex-inv-ls-no-category'],
  columns,
  defaultSortColumn: 'name',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      skuPart(row.sku),
      activePart(row.active ?? row.is_active),
      moneyPart(row.price_excluding_tax ?? row.price_excl_tax),
    ]),
}
